import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RequireAuth } from '@/guards/RequireAuth';
import { RedirectIfAuthenticated } from '@/guards/RedirectIfAuthenticated';
import { RequirePermission } from '@/guards/RequirePermission';
import { AppLayout } from '@/layouts/AppLayout';
import { LoginPage } from '@/pages/auth/LoginPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage';
import { RoutePage } from '@/pages/RoutePage';
import { DashboardPage } from '@/pages/DashboardPage';
import { DomainsPage } from '@/pages/mail/DomainsPage';
import { DomainDetailPage } from '@/pages/mail/DomainDetailPage';
import { MailboxesPage } from '@/pages/mail/MailboxesPage';
import { MailboxDetailPage } from '@/pages/mail/MailboxDetailPage';
import { MigrationsPage } from '@/pages/mail/MigrationsPage';
import { AliasesPage } from '@/pages/mail/AliasesPage';
import { ForwardersPage } from '@/pages/mail/ForwardersPage';
import { QueuePage } from '@/pages/mail/QueuePage';
import { LogsPage } from '@/pages/mail/LogsPage';
import { StatisticsPage } from '@/pages/mail/StatisticsPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { WebHostingPage } from '@/pages/hosting/WebHostingPage';
import { QuotasPage } from '@/pages/mail/QuotasPage';
import { OnboardOrganizationPage } from '@/pages/organizations/OnboardOrganizationPage';
import { AcceptInvitationPage } from '@/pages/organizations/AcceptInvitationPage';
import { TeamPage } from '@/pages/team/TeamPage';
import { HealthPage } from '@/pages/HealthPage';
import { FeatureUnavailable } from '@/components/common/FeatureUnavailable';

/**
 * Route table. Public auth routes are separate from the protected shell (guarded
 * by RequireAuth). Capabilities without a deployed backend render
 * FeatureUnavailable (see config/features.ts).
 */
export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <RedirectIfAuthenticated>
        <LoginPage />
      </RedirectIfAuthenticated>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <RedirectIfAuthenticated>
        <ResetPasswordPage />
      </RedirectIfAuthenticated>
    ),
  },
  // Email-verification deep link from identity mail (/verify?token=…). Public and
  // NOT redirected when signed in: confirming an address must work either way.
  { path: '/verify', element: <VerifyEmailPage /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'team', element: <TeamPage /> },
      // Former Users placeholder: keep old links working.
      { path: 'users', element: <Navigate to="/team" replace /> },
      { path: 'users/:id', element: <Navigate to="/team" replace /> },
      { path: 'domains', element: <DomainsPage /> },
      { path: 'domains/:id', element: <DomainDetailPage /> },
      { path: 'mailboxes', element: <MailboxesPage /> },
      { path: 'mailboxes/:id', element: <MailboxDetailPage /> },
      { path: 'migrations', element: <RequirePermission permission="mailbox:manage"><MigrationsPage /></RequirePermission> },
      { path: 'aliases', element: <AliasesPage /> },
      { path: 'forwarders', element: <ForwardersPage /> },
      { path: 'shared-mailboxes', element: <FeatureUnavailable feature="shared-mailboxes" /> },
      { path: 'distribution-lists', element: <FeatureUnavailable feature="distribution-lists" /> },
      { path: 'queue', element: <RequirePermission anyOf={['security.view', 'security.manage']}><QueuePage /></RequirePermission> },
      { path: 'logs', element: <LogsPage /> },
      { path: 'statistics', element: <StatisticsPage /> },
      { path: 'quotas', element: <QuotasPage /> },
      { path: 'organizations', element: <Navigate to="/organizations/new" replace /> },
      { path: 'organizations/new', element: <OnboardOrganizationPage /> },
      { path: 'invitations/:token/accept', element: <AcceptInvitationPage /> },
      { path: 'web-hosting', element: <RequirePermission permission="site:read"><WebHostingPage /></RequirePermission> },
      // Optional data-plane capabilities whose backends are not deployed (config/features.ts).
      { path: 'reports', element: <FeatureUnavailable feature="reports" /> },
      { path: 'quarantine', element: <FeatureUnavailable feature="quarantine" /> },
      { path: 'mail-tracking', element: <FeatureUnavailable feature="mail-tracking" /> },
      { path: 'threat-dashboard', element: <FeatureUnavailable feature="threat-dashboard" /> },
      { path: 'security-alerts', element: <FeatureUnavailable feature="security-alerts" /> },
      { path: 'attachments', element: <FeatureUnavailable feature="attachments" /> },
      { path: 'url-investigation', element: <FeatureUnavailable feature="url-investigation" /> },
      { path: 'spam-intelligence', element: <FeatureUnavailable feature="spam-intelligence" /> },
      { path: 'policies', element: <FeatureUnavailable feature="policies" /> },
      { path: 'health', element: <HealthPage /> },
      { path: 'settings/:section', element: <SettingsPage /> },
      { path: '*', element: <RoutePage title="Page not found" description="The page you requested does not exist."><p className="text-sm">Check the address, or use the navigation to find what you need.</p></RoutePage> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
